import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {client} from "../../services/api-client";
import {AxiosError} from "axios";
import {IContact, ISocialMedia} from "../../types/generalTypes";
import {toast} from 'react-toastify';


export interface IUpdatePlayerPayload {
    payload: {
        id: number,
        socialMedia: ISocialMedia,
        contact: IContact
    },
    closeForm: () => void
}

export interface ICreatePlayerPayload {
    payload: {
        socialMedia: ISocialMedia,
        contact: IContact
    },
    closeForm: () => void
}

export interface IPlayer {
    id: number,
    name: string,
    surname: string,
    nickname: string,
    displayname: string,
    dateOfBirth: string,
    socialMedia: null | ISocialMedia,
    contact: null | IContact
}


interface IGetPlayersResp {
    totalItems: number,
    pageNum: number,
    pageSize: number,
    message: string,
    isOK: boolean,
    sort: null | string,
    sortField: null | string,
    search: null | string,
    items: IPlayer[]
}

interface IPlayersState {
    players: IPlayer[],
    totalCount: number,
    pageNumber: number,
    itemsPerPage: number,
    pageSize: number
}

const initialState: IPlayersState = {
    players: [],
    totalCount: 0,
    pageNumber: 1,
    itemsPerPage: 25,
    pageSize: 0
};


export const getPlayers = createAsyncThunk<IGetPlayersResp, string, { rejectValue: string }>(
    'players/get-players',
    async (queryParams, {rejectWithValue}) => {
        try {
            const {data} = await client.get(`api/crm/darts-players${queryParams}`);
            return data
        } catch (e) {
            if (e instanceof AxiosError) {
                return rejectWithValue(e.response?.data)
            }
        }
    }
)

export const createPlayer = createAsyncThunk<IPlayer, ICreatePlayerPayload, { rejectValue: string }>(
    'players/create-player',
    async (body, {rejectWithValue}) => {
        try {
            const {data} = await client.post(`api/crm/darts-players`, body.payload);
            body.closeForm();
            toast.info("SDC: Player has been created")
            return data
        } catch (e) {
            if (e instanceof AxiosError) {
                toast.error(e.message)
                return rejectWithValue(e.message)
            }
        }
    }
)

export const updatePlayer = createAsyncThunk<IPlayer, IUpdatePlayerPayload, { rejectValue: string }>(
    'players/update-player',
    async (body, {rejectWithValue}) => {
        try {
            const {data} = await client.put(`api/crm/darts-players`, body.payload);
            body.closeForm();
            toast.info("SDC: Player has been updated")
            return data
        } catch (e) {
            if (e instanceof AxiosError) {
                toast.error(e.message)
                return rejectWithValue(e.message)
            }
        }
    }
)


const playersSlice = createSlice({
    name: 'players',
    initialState,
    reducers: {
        setPageNumber: (state, action: PayloadAction<number>) => {
            state.pageNumber = action.payload;
        },
        setItemsPerPage: (state, action: PayloadAction<number>) => {
            state.itemsPerPage = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getPlayers.fulfilled, (state, {payload}) => {
                state.players = payload.items;
                state.pageNumber = payload.pageNum;
                state.totalCount = payload.totalItems;
                state.pageSize = payload.pageSize
            })
            .addCase(updatePlayer.fulfilled, (state, action) => {
                state.players = state.players.map(player => player.id === action.payload.id ? action.payload : player)
            })
            .addCase(createPlayer.fulfilled, (state, action) => {
                state.players.unshift(action.payload)
            })
    }
});

export const {setPageNumber, setItemsPerPage} = playersSlice.actions;
export default playersSlice.reducer;
